import React, { useEffect, useState } from "react";
import { PlatformEnum, VStack } from "../../../shared/utils";
import { ArrowRight } from "lucide-react";
import { Icon } from "@iconify/react";
import {
  ButtonSize,
  TransparentCTAButton,
} from "../../../shared/CTAButtonComponents";
import Select from "react-select";
import { OptionType, SelectDropdown } from "../../../shared/SelectDropdown";
import { useNavigate } from "react-router-dom";
import { useUpdateUserInfo } from "../../../queries/auth";
import { AnalyticsConstant } from "../../../AnalyticsContant";
import { handleClickForTracking } from "../../../analytics";
import { trackEvent } from "../../../utils/AmpAnalytics";
import _ from "lodash";

type CardProps = {
  title: string;
  description: string;
  imageUrl: string;
  bgColor: string;
  titleBgColor: string;
  arrowColor: string;
  titleClassName?: string;
  redirectURL: string;
  access?: boolean;
  companies?: any[];
  buttonType?: "button" | "dropdown";
  platform: PlatformEnum;
};

const ProductMainCard: React.FC<CardProps> = ({
  title,
  description,
  imageUrl,
  bgColor,
  titleBgColor,
  titleClassName,
  redirectURL,
  buttonType,
  access,
  companies,
  platform,
}) => {
  const handleRedirect = (redirectURL: string) => {
    sessionStorage.clear();
    window.location.href = selectedCompany
      ? `${redirectURL}?companyId=${selectedCompany.value}`
      : `${redirectURL}`;
  };
  const navigate = useNavigate();

  const {mutate: updateUserInfo} = useUpdateUserInfo()
  const companiesOptions: OptionType[] = [];
  companies?.forEach((company) =>
    companiesOptions.push({
      value: company.id,
      label: company.name,
      secondaryLabel: company.dba,
    })
  );

  useEffect(() => {
    if (companiesOptions?.length > 0 && selectedCompany === undefined)
      setSelectedCompany(companiesOptions[0]);
  }, [companiesOptions]);

  const [selectedCompany, setSelectedCompany] = useState<OptionType>();

  return (
    <div
      className={`rounded-xl shadow-md overflow-hidden flex flex-wrap items-start w-full md:min-w-[385px] min-h-[190px] relative pr-6 border ${
        access !== undefined && !access ? "" : "cursor-pointer"
      }`}
      style={{ backgroundColor: bgColor }}
      onClick={(event) => {
        const dropdownTarget = event.target as Element;
        const isDropdownInteraction =
          dropdownTarget.closest("[data-product-card-dropdown]") ||
          dropdownTarget.closest(".product-card-select__menu-portal");

        if (isDropdownInteraction) return;

        if (access){
          updateUserInfo({data: {lastUsedProduct: platform}})
          // handleClickForTracking(AnalyticsConstant[`${platform}_redirect_button`]);
          trackEvent(`Navigating To ${_.capitalize(platform)}`);
          handleRedirect(redirectURL);
        }
      }}
    >
      {/* Title */}
      <div className="absolute -top-[60%] -left-[30%] w-full h-1/6 flex items-center justify-center">
        <div className="relative">
          <Icon icon="mdi:ellipse" color={titleBgColor} height={520} />
          <span
            className={`absolute bottom-20 left-1/2 transform -translate-x-1/3 mb-10 text-white font-semibold ${titleClassName}`}
          >
            {title?.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Description */}
      <VStack>
        <div className="px-6 pt-6 flex-1 mt-16 mr-10 w-[325px]">
          <p className="text-gray-700 text-xs text-wrap">{description}</p>
        </div>
        {access === undefined && (
          <>
            {buttonType === "dropdown" ? (
              <div className="w-[200px] ml-6 mt-2">
                <div className="animate-pulse h-8 rounded-md border border-gray-200 p-3 flex items-center justify-between">
                  <div className="col-span-2 h-2 rounded bg-gray-200 w-2/3"></div>
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            ) : (
              <div className="w-20 ml-6 mt-2">
                <div className="animate-pulse h-8 rounded-md border border-gray-200 p-3">
                  <div className="col-span-2 h-2 rounded bg-gray-200"></div>
                </div>
              </div>
            )}
          </>
        )}
        {access !== undefined && !access && (
          <TransparentCTAButton
            className="w-20 ml-6 z-20 flex justify-start text-center rounded-md px-auto text-primary border-primary py-2 mt-2 hover:border-primary"
            buttonSize={ButtonSize.SMALL}
            type="button"
            onClick={() => {
              //  handleClickForTracking(AnalyticsConstant[`${platform}_explore_button`]);
              trackEvent(`Viewing ${_.capitalize(platform)} Explore Page`);
              navigate(`/landing/explore/${platform}`)}}
          >
            <span className="w-full text-xs leading-0">{"Explore"}</span>
          </TransparentCTAButton>
        )}
        {buttonType === "dropdown" && access !== undefined && access && (
          <div
            onClick={(event) => event.stopPropagation()}
            data-product-card-dropdown
            className="w-[200px] relative mt-1"
            style={{ backgroundColor: bgColor }}
          >
            <Select
              options={companiesOptions}
              menuPortalTarget={document.body}
              classNamePrefix="product-card-select"
              className={`w-[200px] ml-6 cursor-pointer`}
              value={selectedCompany}
              onChange={(e: any) => {
                setSelectedCompany(e);
              }}
              styles={{
                control: (provided, state: any) => ({
                  ...provided,
                  backgroundColor: "transparent",
                  minHeight: "35px",
                  height: "35px",
                }),
                singleValue: (provided) => ({
                  ...provided,
                  fontSize: "12px",
                  color: "#333",
                }),

                valueContainer: (provided) => ({
                  ...provided,
                  padding: "0px 8px",
                  fontSize: "12px",
                }),
                indicatorsContainer: (provided) => ({
                  ...provided,
                  height: "35px",
                  color: "#0C224A",
                }),
                menu: (provided) => ({
                  ...provided,
                  fontSize: "14px",
                }),
                option: (provided, state) => ({
                  ...provided,
                  fontSize: "12px",
                  padding: "8px 12px",
                }),
              }}
            />
          </div>
          // <div
          //   onClick={(event) => event.stopPropagation()}
          //   className="w-[200px] relative"
          //   style={{ backgroundColor: bgColor }}
          // >
          //   <SelectDropdown
          //     options={companyOptions || []}
          //     setSelectedOption={() => {}}
          //     selectedOption={companyOptions[0]}
          //     className={`bg-${bgColor} ml-6 w-[150px] mt-2`}
          //   />
          // </div>
        )}
      </VStack>

      <VStack>
        {/* Arrow Icon */}
        <div className={`absolute top-4 right-4 ${access !== undefined && !access ? "" : "cursor-pointer"}`}>
          <ArrowRight className="text-gray-600" />
        </div>

        {/* Chart Image */}
        <div className="absolute bottom-0 right-0 mt-2 flex items-end justify-center">
          <img
            src={imageUrl}
            alt="Chart"
            className="w-20 h-20 object-contain"
          />
        </div>
      </VStack>
    </div>
  );
};

export default ProductMainCard;
